<template>
  <div class="bto-sb-widget bto-sb-widget-streaming-channel">
    <div class="bto-sb-widget-title">
      <h3>{{ t("Widget.LiveChanelOnlineTitle") }}</h3>
    </div>
    <div v-if="userLoggedIn()" class="bto-sb-widget-content">
      <div class="bto-sb-streaming-nav">
        <div class="bto-sb-scrolling-menu">
          <ul>
            <li
              v-for="(pid, pName) in getChannels"
              :key="pName"
              :class="{ active: selectedStreamProvider === pid }">
              <a @click="changeProvider(pid)">{{ t(`M.Channel${pid}`) }}</a>
            </li>
          </ul>
        </div>
      </div>
      <template v-if="Object.keys(sports).length > 0">
        <sports-list-component
          :sports="sports"
          :useRawSports="true"
          @changeSport="selectSport"
          :selected-sport="selectedSport"
          :scrollTriger="6" />
        <div class="bto-sb-streaming-content">
          <div data-bto-lco-fixture>
            <div
              class="bto-sb-streaming-event-item"
              v-for="(tName, tid) in getTournamnets"
              :key="tid">
              <div class="bto-sb-streaming-event-header">
                {{ tName }}
              </div>
              <div v-for="match in getMatches(tid)" :key="match.externalEventId">
                <div
                  class="bto-sb-streaming-event"
                  :class="{ 'bto-sb-streaming-event-live': match.isLive }"
                  @click="openMatch(match)">
                  <div class="bto-sb-mb-streaming-event-status">
                    <i class="bto-app-live-stream-o"></i>
                  </div>
                  <div class="bto-sb-streaming-event-name">
                    <span class="bto-sb-event-home">{{
                      match.homeTeam || match.categoryName + " - " + match.tournamentName
                    }}</span>
                    <span class="bto-sb-event-away">{{ match.awayTeam }}</span>
                  </div>
                  <div
                    v-if="
                      selectedStreamProvider === STREAM_PROVIDERS.SIS ||
                      !match.isLive ||
                      STREAM_PROVIDERS.RMG
                    "
                    class="bto-sb-mb-streaming-event-date">
                    {{ match.date }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <inline-message v-else type="info" :message="t('Public.NoEventsAvailable')" />
    </div>
    <inline-message v-else type="warning" :message="t('Mobile.LoginFirst')" />
  </div>
</template>

<script>
  import store from '../../../store/index';
  import SportsListComponent from '../../common/match/sports-list-component';
  import { urlFriendlyParam } from '@bto-services/utils';
  import { getStreamingMatches } from './service-video-streaming';
  import { STREAM_PROVIDERS } from '../../../models/livestream/livestream.const';
  import { userLoggedIn } from '@bto-services/user';

  export default {
    name: 'LiveChannelOnlineWidget',
    data() {
      return {
        groupedSports: {},
        groupedTournaments: {},
        selectedSportId: '',
        selectedProvider: null,
        STREAM_PROVIDERS,
      };
    },
    props: {
      getOnlyAvailableChannels: {
        type: Boolean,
        default: false,
        label: 'Show only Available channels',
        description: 'Flag whether to Show only Available channels that have stream matches inside',
      },
      channels: {
        type: String,
        default: 'Betradar,IMGArena,Statsperform,SIS,Oddin,RMG',
        label: 'List of channels to show',
        description: 'List of channels to show in the widget (Options: Betradar, IMGArena, Statsperform, SIS, Oddin, RMG)',
      },
    },
    computed: {
      getChannels() {
        if (!this.channels) {
          return STREAM_PROVIDERS;
        }

        if (this.getOnlyAvailableChannels) {
          return Object.values(STREAM_PROVIDERS).filter((id) => store.state.LiveStreamingModule.integrationBrokerId[id]);
        }

        return this.channels
          .split(',')
          .map((c) => c.trim())
          .filter((c) => STREAM_PROVIDERS[c])
          .map((c) => STREAM_PROVIDERS[c]);
      },
      sports() {
        return this.groupedSports;
      },
      getTournamnets() {
        return this.sports[this.selectedSport] ? this.sports[this.selectedSport].tournaments : {};
      },
      initalLoad() {
        return store.state.LiveStreamingModule.initalLoad;
      },
      selectedSport() {
        return this.selectedSportId;
      },
      selectedStreamProvider() {
        return this.selectedProvider;
      },
    },
    created() {
      if (!userLoggedIn()) {
        return;
      }
      store.dispatch('getAllLiveStreamingMatches');
    },
    methods: {
      getMatches(tid) {
        return this.groupedTournaments[tid];
      },
      changeProvider(providerId) {
        this.selectedProvider = providerId;
        getStreamingMatches(providerId).then(this.setStreamingMatches).catch(this.resetProviderStreamingState);
      },
      setStreamingMatches(data) {
        const { groupedSports, groupedTournaments } = data;

        this.selectedSportId = Object.keys(groupedSports)[0] || '';
        this.groupedSports = groupedSports;
        this.groupedTournaments = groupedTournaments;
      },
      resetProviderStreamingState() {
        this.groupedSports = {};
        this.groupedTournaments = {};
        this.selectedSportId = '';
        this.selectedProvider = null;
      },
      selectSport(sportId) {
        this.selectedSportId = sportId;
      },
      openMatch(match) {
        if (!match.isLive) {
          return;
        }
        const sportName = urlFriendlyParam(match.sportName);
        const categoryName = urlFriendlyParam(match.tournamentName);
        const eventName = urlFriendlyParam(`${match.homeTeam} vs ${match.awayTeam}`);
        if (match.internalEventId) {
          this.$router.push({
            name: 'seoGameLive',
            params: { sportName, categoryName, eventName, matchId: match.internalEventId },
          });
          return;
        }
        const eeid = `eeid:${match.externalEventId}`;

        if (match.integrationBrokerId === STREAM_PROVIDERS.SIS || match.integrationBrokerId === STREAM_PROVIDERS.RMG) {
          this.$router.push({
            name: 'liveRace',
            params: match.integrationBrokerId === STREAM_PROVIDERS.SIS
              ? { sportName, categoryName, matchId: eeid, providerId: match.integrationBrokerId }
              : { sportName, categoryName, matchId: eeid, providerId: match.integrationBrokerId, rmgStreamId: match.streamId, },
          });
          return;
        }

        this.$router.push({
          name: 'seoGameLive',
          params: { sportName, categoryName, eventName, matchId: eeid },
        });
      },
      userLoggedIn,
    },
    components: {
      SportsListComponent,
    },
    watch: {
      initalLoad: {
        handler(val) {
          if (val) {
            this.changeProvider(this.getChannels[0]);
          }
        },
        immediate: true,
      },
    },
  };
</script>
<style scoped>
  div[data-bto-lco-fixture] {
    max-height: 276px;
    overflow-y: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  div[data-bto-lco-fixture]::-webkit-scrollbar {
    display: none;
  }
</style>
